import { useEffect, useState, useRef } from 'react';

/**
 * Performance monitoring utility for EventList component
 * Tracks rendering times, memory usage, and provides optimization metrics
 */
export const usePerformanceMonitor = (dataSize, isEnabled = false) => {
  const [metrics, setMetrics] = useState({
    renderTime: 0,
    memoryUsage: 0,
    lastUpdate: null,
    totalReRenders: 0,
    averageRenderTime: 0
  });
  
  const renderStartTime = useRef(null);
  const renderTimes = useRef([]);
  const reRenderCount = useRef(0);

  // Start performance measurement
  useEffect(() => {
    if (!isEnabled) return;
    
    renderStartTime.current = performance.now();
    reRenderCount.current += 1;
  });

  // End performance measurement
  useEffect(() => {
    if (!isEnabled || !renderStartTime.current) return;
    
    const renderTime = performance.now() - renderStartTime.current;
    renderTimes.current.push(renderTime);
    
    // Keep only last 10 render times for average calculation
    if (renderTimes.current.length > 10) {
      renderTimes.current.shift();
    }
    
    const averageRenderTime = renderTimes.current.reduce((sum, time) => sum + time, 0) / renderTimes.current.length;
    
    // Get memory usage if available
    const memoryInfo = performance.memory ? {
      used: performance.memory.usedJSHeapSize,
      total: performance.memory.totalJSHeapSize,
      limit: performance.memory.jsHeapSizeLimit
    } : null;
    
    setMetrics({
      renderTime: renderTime.toFixed(2),
      memoryUsage: memoryInfo ? (memoryInfo.used / 1024 / 1024).toFixed(2) : 0,
      lastUpdate: new Date().toISOString(),
      totalReRenders: reRenderCount.current,
      averageRenderTime: averageRenderTime.toFixed(2),
      dataSize,
      memoryInfo
    });

    renderStartTime.current = null;
  }, [isEnabled, dataSize]);

  return metrics;
};

/**
 * Performance alert component that shows warnings for large datasets
 */
export const PerformanceAlert = ({ dataSize, onOptimize }) => {
  if (dataSize < 1000) return null;
  
  const isLargeDataset = dataSize > 10000;
  const isVeryLargeDataset = dataSize > 100000;
  
  if (isVeryLargeDataset) {
    return (
      <div style={{
        backgroundColor: '#ff6b6b',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '4px',
        margin: '8px 0',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <span>
          ⚠️ Very Large Dataset ({dataSize.toLocaleString()} rows) - Performance optimizations active
        </span>
        {onOptimize && (
          <button onClick={onOptimize} style={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            border: 'none',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '3px',
            cursor: 'pointer'
          }}>
            More Options
          </button>
        )}
      </div>
    );
  }
  
  if (isLargeDataset) {
    return (
      <div style={{
        backgroundColor: '#ffa726',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '4px',
        margin: '8px 0',
        fontSize: '14px'
      }}>
        ⚡ Large Dataset ({dataSize.toLocaleString()} rows) - Virtual scrolling enabled for optimal performance
      </div>
    );
  }
  
  return null;
};

/**
 * Debug performance metrics display (only in development)
 */
export const PerformanceDebug = ({ metrics }) => {
  if (process.env.NODE_ENV !== 'development' || !metrics.lastUpdate) return null;
  
  return (
    <div style={{
      position: 'fixed',
      top: '10px',
      right: '10px',
      backgroundColor: 'rgba(0,0,0,0.8)',
      color: 'white',
      padding: '10px',
      borderRadius: '5px',
      fontSize: '12px',
      zIndex: 9999,
      fontFamily: 'monospace',
      minWidth: '200px'
    }}>
      <h4 style={{ margin: '0 0 5px 0', fontSize: '14px' }}>EventList Performance</h4>
      <div>Data Size: {metrics.dataSize?.toLocaleString()}</div>
      <div>Last Render: {metrics.renderTime}ms</div>
      <div>Average: {metrics.averageRenderTime}ms</div>
      <div>Re-renders: {metrics.totalReRenders}</div>
      <div>Memory: {metrics.memoryUsage}MB</div>
      {metrics.memoryInfo && (
        <div style={{ fontSize: '10px', marginTop: '5px', opacity: 0.8 }}>
          Heap: {(metrics.memoryInfo.used / 1024 / 1024).toFixed(1)}MB / {(metrics.memoryInfo.total / 1024 / 1024).toFixed(1)}MB
        </div>
      )}
    </div>
  );
};

const performanceUtils = {
  usePerformanceMonitor,
  PerformanceAlert,
  PerformanceDebug
};

export default performanceUtils;